import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  InputAdornment,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardHeader,
  Fade,
  Skeleton,
  alpha
} from '@mui/material';
import { 
  Search as SearchIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  History as HistoryIcon,
  FilterList as FilterListIcon,
  DateRange as DateRangeIcon,
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  AccessTime as ClockIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { formatDate, getInitials } from '../utils/helpers';
import { LoadingSpinner } from '../components';

const Dashboard = () => {
  
  const [studentName, setStudentName] = useState('');
  const [licenseId, setLicenseId] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [guestNotifications, setGuestNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filtering state
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('today');

  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };
  
  // Fetch guest notifications from database
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        
        // Get pending guest approvals as notifications
        const pendingGuests = await apiService.guests.getPendingApproval();
        
        // Format the notifications for display
        const notifications = pendingGuests.map(guest => ({
          id: guest.id,
          guestName: guest.name,
          parentName: guest.initiated_by_guardian_id, // We'd need to lookup the guardian name
          time: new Date(guest.created_at?.seconds ? guest.created_at.seconds * 1000 : guest.created_at).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }),
          date: new Date(guest.created_at?.seconds ? guest.created_at.seconds * 1000 : guest.created_at),
          status: guest.approval_status || 'pending'
        }));
        
        setGuestNotifications(notifications);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching notifications:', error);
        setLoading(false);
      }
    };
    
    fetchNotifications();
    
    // Set up real-time listener for guest notifications
    try {
      const storedUserData = localStorage.getItem('safeline_user_data');
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        console.log('Dashboard: User data for notifications:', userData);
        
        // For developer users, we might not have a school_id, so skip real-time for now
        if (userData?.school_id) {
          const unsubscribe = apiService.realtime.listenToGuestNotifications(
            userData.school_id,
            (guests) => {
              const notifications = guests.map(guest => ({
                id: guest.id,
                guestName: guest.name,
                parentName: guest.initiated_by_guardian_id,
                time: new Date(guest.created_at?.seconds ? guest.created_at.seconds * 1000 : guest.created_at).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                }),
                date: new Date(guest.created_at?.seconds ? guest.created_at.seconds * 1000 : guest.created_at),
                status: guest.approval_status || 'pending'
              }));
              setGuestNotifications(notifications);
            }
          );
          
          return () => unsubscribe && unsubscribe();
        } else {
          console.log('Dashboard: No school_id found for user, skipping real-time notifications');
        }
      }
    } catch (error) {
      console.error('Dashboard: Error setting up real-time notifications:', error);
    }
  }, []);
  
  // Filter notifications based on status and date
  const filteredNotifications = guestNotifications.filter(notification => {
    // Status filter
    if (statusFilter !== 'all' && notification.status !== statusFilter) {
      return false;
    }
    
    // Date filter
    if (dateFilter === 'today') {
      const today = new Date();
      return notification.date.toDateString() === today.toDateString();
    } else if (dateFilter === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return notification.date.toDateString() === yesterday.toDateString();
    } else if (dateFilter === 'this_week') {
      const today = new Date();
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      return notification.date >= weekStart;
    }
    
    return true;
  });
  
  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    
    // Simulate API call
    setTimeout(() => {
      // Shuffle the notifications to simulate new data
      setGuestNotifications(prev => [...prev].sort(() => Math.random() - 0.5));
      setRefreshing(false);
    }, 1000);
  };
  
  const handleSearch = async () => {
    try {
      setSearched(false);
      setSearching(true);
      
      if (!studentName && !licenseId) {
        setSearchResults([]);
        setSearched(true);
        setSearching(false);
        return;
      }
      
      // Use our database service to search pickup logs
      const logs = await apiService.pickupLogs.search({ 
        studentName, 
        licenseId 
      });
      
      // Format the results for display
      const formattedResults = logs.map(log => ({
        id: log.id,
        studentName: log.student_name,
        grade: log.grade,
        guardianName: log.guardian_name,
        licenseId: log.license_plate_id.split('_').pop(), // Extract just the plate number
        timestamp: log.timestamp,
        status: log.status
      }));
      
      setSearchResults(formattedResults);
      setSearched(true);
      setSearching(false);
    } catch (error) {
      console.error('Error searching pickup logs:', error);
      setSearchResults([]);
      setSearched(true);
      setSearching(false);
    }
  };

  const getCustomStatusColor = (status) => {
    switch (status) {
      case 'arrived':
      case 'completed':
      case 'approved':
        return {
          backgroundColor: safeLineColors.lightEmerald,
          color: safeLineColors.forestGreen,
          borderColor: safeLineColors.emerald
        };
      case 'pending':
        return {
          backgroundColor: safeLineColors.lightAmber,
          color: safeLineColors.darkAmber,
          borderColor: safeLineColors.amber
        };
      case 'denied':
      case 'error':
        return {
          backgroundColor: safeLineColors.lightRed,
          color: safeLineColors.darkRed,
          borderColor: safeLineColors.red
        };
      default:
        return {
          backgroundColor: safeLineColors.mediumGray,
          color: safeLineColors.darkCharcoal,
          borderColor: safeLineColors.coolGray
        };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'arrived':
      case 'completed':
      case 'approved':
        return <CheckCircleIcon sx={{ fontSize: 16, color: safeLineColors.emerald }} />;
      case 'pending':
        return <ClockIcon sx={{ fontSize: 16, color: safeLineColors.amber }} />;
      case 'denied':
      case 'error':
        return <ErrorIcon sx={{ fontSize: 16, color: safeLineColors.red }} />;
      default:
        return <WarningIcon sx={{ fontSize: 16, color: safeLineColors.mutedGray }} />;
    }
  };
  
  return (
    <Box sx={{ backgroundColor: safeLineColors.lightGray, minHeight: '100vh', width: '100%', flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        borderTop: `4px solid ${safeLineColors.flameOrange}`,
        backgroundColor: safeLineColors.pureWhite,
        borderRadius: '8px 8px 0 0',
        border: `1px solid ${safeLineColors.coolGray}`,
        p: 3,
        mb: 3
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 600,
            color: safeLineColors.darkCharcoal,
            mb: 1
          }}
        >
          Verification Dashboard
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: safeLineColors.mediumCharcoal
          }}
        >
          Manage student verification and guest notifications
        </Typography>
      </Box>
      
      {/* Verification Panel */}
      <Card 
        elevation={3} 
        sx={{ 
          mb: 4, 
          borderRadius: 2,
          transition: 'all 0.3s ease',
          backgroundColor: safeLineColors.pureWhite,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${safeLineColors.coolGray}`,
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 15px 35px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)'
          },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
          }
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2,
                backgroundColor: safeLineColors.flameOrange,
                mr: 2
              }}>
                <BadgeIcon sx={{ color: safeLineColors.pureWhite, fontSize: 24 }} />
              </Box>
              <Typography variant="h6" fontWeight="bold" sx={{ color: safeLineColors.darkCharcoal }}>
                Verification
              </Typography>
            </Box>
          }
          sx={{ 
            backgroundColor: safeLineColors.orangeTint,
            borderBottom: `1px solid ${safeLineColors.coolGray}`,
            pb: 2,
            pt: 2
          }}
        />
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Name / Grade"
                placeholder="Student(s) / Guests"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: safeLineColors.flameOrange }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    backgroundColor: safeLineColors.pureWhite,
                    '&:hover': {
                      backgroundColor: safeLineColors.pureWhite
                    },
                    boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease'
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: safeLineColors.coolGray,
                      transition: 'all 0.2s ease'
                    },
                    '&:hover fieldset': {
                      borderColor: safeLineColors.flameOrange
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: safeLineColors.flameOrange,
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: safeLineColors.mediumCharcoal,
                    '&.Mui-focused': {
                      color: safeLineColors.flameOrange
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Parent / License ID"
                placeholder="Parent / License"
                value={licenseId}
                onChange={(e) => setLicenseId(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon sx={{ color: safeLineColors.flameOrange }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    backgroundColor: safeLineColors.pureWhite,
                    '&:hover': {
                      backgroundColor: safeLineColors.pureWhite
                    },
                    boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease'
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: safeLineColors.coolGray,
                      transition: 'all 0.2s ease'
                    },
                    '&:hover fieldset': {
                      borderColor: safeLineColors.flameOrange
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: safeLineColors.flameOrange,
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: safeLineColors.mediumCharcoal,
                    '&.Mui-focused': {
                      color: safeLineColors.flameOrange
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button 
                variant="contained" 
                fullWidth 
                sx={{ 
                  height: '56px',
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                  boxShadow: `0 4px 10px ${alpha(safeLineColors.flameOrange, 0.3)}`,
                  fontWeight: 'bold',
                  letterSpacing: '0.5px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: `linear-gradient(90deg, ${safeLineColors.deepOrange}, ${safeLineColors.flameOrange})`,
                    boxShadow: `0 6px 15px ${alpha(safeLineColors.flameOrange, 0.4)}`,
                    transform: 'translateY(-2px)'
                  },
                  '&:active': {
                    transform: 'translateY(0)'
                  }
                }}
                onClick={handleSearch}
                startIcon={<SearchIcon />}
              >
                {searching ? <LoadingSpinner message="Searching..." /> : 'Check I/O History'}
              </Button>
            </Grid>
          </Grid>
        
          {/* Search Results */}
          {searched && (
            <Fade in={searched} timeout={500}>
              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium', color: safeLineColors.darkCharcoal }}>
                    Results
                  </Typography>
                  <Chip 
                    icon={<SearchIcon />} 
                    label={`${searchResults.length} records found`} 
                    sx={{
                      backgroundColor: safeLineColors.flameOrange,
                      color: safeLineColors.pureWhite,
                      fontWeight: 'medium'
                    }}
                    size="small"
                  />
                </Box>
                
                {searchResults.length > 0 ? (
                  <TableContainer sx={{ 
                    borderRadius: 2,
                    border: `1px solid ${safeLineColors.coolGray}`,
                    backgroundColor: safeLineColors.pureWhite
                  }}>
                    <Table>
                      <TableHead sx={{ backgroundColor: safeLineColors.mediumGray }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', color: safeLineColors.darkCharcoal }}>Student</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: safeLineColors.darkCharcoal }}>Grade</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: safeLineColors.darkCharcoal }}>Guardian</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: safeLineColors.darkCharcoal }}>License ID</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: safeLineColors.darkCharcoal }}>Time</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: safeLineColors.darkCharcoal }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {searchResults.map((row) => (
                          <TableRow 
                            key={row.id} 
                            hover
                            sx={{
                              '&:hover': {
                                backgroundColor: safeLineColors.lightGray
                              }
                            }}
                          >
                            <TableCell sx={{ color: safeLineColors.darkCharcoal }}>{row.studentName}</TableCell>
                            <TableCell sx={{ color: safeLineColors.mediumCharcoal }}>{row.grade}</TableCell>
                            <TableCell sx={{ color: safeLineColors.mediumCharcoal }}>{row.guardianName}</TableCell>
                            <TableCell sx={{ color: safeLineColors.mediumCharcoal, fontFamily: 'monospace' }}>{row.licenseId}</TableCell>
                            <TableCell sx={{ color: safeLineColors.mediumCharcoal }}>{formatDate(row.timestamp)}</TableCell>
                            <TableCell>
                              <Chip 
                                icon={getStatusIcon(row.status)}
                                label={row.status.charAt(0).toUpperCase() + row.status.slice(1)} 
                                size="small"
                                sx={{
                                  ...getCustomStatusColor(row.status),
                                  border: `1px solid ${getCustomStatusColor(row.status).borderColor}`,
                                  fontWeight: 'medium'
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    border: `2px dashed ${safeLineColors.coolGray}`,
                    borderRadius: 2,
                    backgroundColor: safeLineColors.lightGray
                  }}>
                    <SearchIcon sx={{ fontSize: 48, color: safeLineColors.mutedGray, mb: 2 }} />
                    <Typography variant="h6" sx={{ color: safeLineColors.mediumCharcoal, mb: 1 }}>
                      No results found
                    </Typography>
                    <Typography variant="body2" sx={{ color: safeLineColors.mutedGray }}>
                      Please try different search criteria
                    </Typography>
                  </Box>
                )}
              </Box>
            </Fade>
          )}
        </CardContent>
      </Card>
      
      {/* Guest Notification List */}
      <Card 
        elevation={3} 
        sx={{ 
          borderRadius: 2,
          transition: 'all 0.3s ease',
          backgroundColor: safeLineColors.pureWhite,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${safeLineColors.coolGray}`,
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 15px 35px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)'
          },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
          }
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2,
                  backgroundColor: safeLineColors.flameOrange,
                  mr: 2
                }}>
                  <NotificationsIcon sx={{ color: safeLineColors.pureWhite, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: safeLineColors.darkCharcoal }}>
                    Guest Notification List
                  </Typography>
                  <Typography variant="body2" sx={{ color: safeLineColors.mediumCharcoal }}>
                    Real-time pickup notifications
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip 
                  icon={<HistoryIcon />} 
                  label="Live Updates" 
                  size="small"
                  sx={{ 
                    backgroundColor: safeLineColors.lightEmerald,
                    color: safeLineColors.forestGreen,
                    border: `1px solid ${safeLineColors.emerald}`,
                    fontWeight: 'medium',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                  }}
                />
                <Chip 
                  label={`${filteredNotifications.length} notifications`} 
                  size="small"
                  sx={{
                    backgroundColor: safeLineColors.flameOrange,
                    color: safeLineColors.pureWhite,
                    fontWeight: 'medium'
                  }}
                />
              </Box>
            </Box>
          }
          sx={{ 
            backgroundColor: safeLineColors.orangeTint,
            borderBottom: `1px solid ${safeLineColors.coolGray}`,
            pb: 2,
            pt: 2
          }}
        />
        
        <CardContent sx={{ p: 0 }}>
          {/* Filters */}
          <Box sx={{ 
            p: 3, 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            borderBottom: `1px solid ${safeLineColors.coolGray}`,
            backgroundColor: safeLineColors.orangeTint
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterListIcon sx={{ mr: 1, color: safeLineColors.flameOrange }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  mr: 2, 
                  fontWeight: 'medium',
                  color: safeLineColors.mediumCharcoal
                }}
              >
                Filters:
              </Typography>
            </Box>
            
            <FormControl 
              size="small" 
              sx={{ 
                minWidth: 150,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: safeLineColors.pureWhite,
                  '& fieldset': {
                    borderColor: safeLineColors.coolGray
                  },
                  '&:hover fieldset': {
                    borderColor: safeLineColors.flameOrange
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: safeLineColors.flameOrange,
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  color: safeLineColors.mediumCharcoal,
                  '&.Mui-focused': {
                    color: safeLineColors.flameOrange
                  }
                }
              }}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="arrived">Arrived</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="denied">Denied</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl 
              size="small" 
              sx={{ 
                minWidth: 150,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: safeLineColors.pureWhite,
                  '& fieldset': {
                    borderColor: safeLineColors.coolGray
                  },
                  '&:hover fieldset': {
                    borderColor: safeLineColors.flameOrange
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: safeLineColors.flameOrange,
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  color: safeLineColors.mediumCharcoal,
                  '&.Mui-focused': {
                    color: safeLineColors.flameOrange
                  }
                }
              }}
            >
              <InputLabel>Time Period</InputLabel>
              <Select
                value={dateFilter}
                label="Time Period"
                onChange={(e) => setDateFilter(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <DateRangeIcon fontSize="small" sx={{ mr: 1, color: safeLineColors.flameOrange }} />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="this_week">This Week</MenuItem>
              </Select>
            </FormControl>
            
            <Box sx={{ flexGrow: 1 }} />
            
            <Button 
              size="small" 
              startIcon={<RefreshIcon />} 
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ 
                minWidth: 100,
                borderRadius: 2,
                background: refreshing 
                  ? safeLineColors.mutedGray 
                  : `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                color: safeLineColors.pureWhite,
                fontWeight: 'medium',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                '&:hover': {
                  background: refreshing 
                    ? safeLineColors.mutedGray 
                    : `linear-gradient(90deg, ${safeLineColors.deepOrange}, ${safeLineColors.flameOrange})`,
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                },
                '&:disabled': {
                  color: safeLineColors.pureWhite,
                  background: safeLineColors.mutedGray
                }
              }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
          
          {/* Notification List */}
          {loading ? (
            <Box sx={{ p: 3 }}>
              {[1, 2, 3, 4].map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 2 }}>
                  <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
                  <Box sx={{ width: '100%' }}>
                    <Skeleton variant="text" width="40%" height={24} />
                    <Skeleton variant="text" width="60%" height={20} />
                  </Box>
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                </Box>
              ))}
            </Box>
          ) : filteredNotifications.length > 0 ? (
            <List sx={{ p: 0 }}>
              {filteredNotifications.map((notification) => (
              <ListItem 
                key={notification.id}
                divider
                sx={{ 
                  py: 2,
                  px: 3,
                  '&:hover': {
                    backgroundColor: safeLineColors.lightGray
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ 
                    bgcolor: safeLineColors.flameOrange,
                    color: safeLineColors.pureWhite,
                    fontWeight: 'bold'
                  }}>
                    {getInitials(notification.guestName, 1)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 'medium', 
                      color: safeLineColors.darkCharcoal 
                    }}>
                      {notification.guestName}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body2" sx={{ 
                      color: safeLineColors.mediumCharcoal 
                    }}>
                      Parent: {notification.parentName}
                    </Typography>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{ color: safeLineColors.mutedGray }}>
                    {notification.time}
                  </Typography>
                  <Chip 
                    icon={getStatusIcon(notification.status)}
                    label={notification.status.charAt(0).toUpperCase() + notification.status.slice(1)} 
                    size="small"
                    sx={{
                      ...getCustomStatusColor(notification.status),
                      border: `1px solid ${getCustomStatusColor(notification.status).borderColor}`,
                      fontWeight: 'medium'
                    }}
                  />
                </Box>
              </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ 
              p: 6, 
              textAlign: 'center',
              backgroundColor: safeLineColors.orangeTint
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                mb: 3
              }}>
                <NotificationsIcon 
                  sx={{ 
                    fontSize: 64, 
                    color: safeLineColors.mutedGray,
                    mb: 2
                  }} 
                />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 'medium',
                    color: safeLineColors.mediumCharcoal,
                    mb: 1
                  }}
                >
                  No notifications found
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: safeLineColors.mutedGray,
                    maxWidth: 400,
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  There are no notifications matching your current filter settings. Try changing your filters or check back later.
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                size="medium" 
                onClick={() => {
                  setStatusFilter('all');
                  setDateFilter('all');
                }}
                startIcon={<FilterListIcon />}
                sx={{ 
                  borderRadius: 2,
                  borderWidth: 2,
                  borderColor: safeLineColors.flameOrange,
                  color: safeLineColors.flameOrange,
                  fontWeight: 'medium',
                  '&:hover': {
                    borderWidth: 2,
                    backgroundColor: alpha(safeLineColors.flameOrange, 0.1),
                    borderColor: safeLineColors.deepOrange,
                    color: safeLineColors.deepOrange
                  }
                }}
              >
                Clear Filters
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;